import { useState, useCallback, useEffect } from "react"
import { isEqual, pickBy, chain } from "lodash"
import moment from "moment"

const LOCAL_STORAGE_KEY = "ompnt-ai-summaries"
const EMPTY_OBJECT = {}

function getLocalStorageSummaries() {
  const localStorageSummaries = localStorage.getItem(LOCAL_STORAGE_KEY)
  try {
    return JSON.parse(localStorageSummaries)
  } catch {
    return EMPTY_OBJECT
  }
}

function saveToStorage(data) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data))
    return true
  } catch {
    return false
  }
}

export default function useLocalStorageSummaries(clearOldSummaries = true) {
  const [error, setError] = useState(null)
  const [summaries, setSummaries] = useState(() => {
    let localStorageSummaries = getLocalStorageSummaries()
    // Remove summaries older than 1 day
    if (localStorageSummaries && clearOldSummaries) {
      localStorageSummaries = pickBy(localStorageSummaries, ({ timestamp }) => moment().diff(moment(timestamp, "x"), "days") < 1)
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(localStorageSummaries))
    }
    return localStorageSummaries
  })

  const setLocalStorageSummary = useCallback((key, value) => {
    const localStorageSummaries = getLocalStorageSummaries()

    let newValue = {
      ...localStorageSummaries,
      [key]: value,
    }

    if (saveToStorage(newValue)) {
      setError(null)
    } else {
      setError(true)
      // If storage is full, gradually remove oldest items until it fits
      let remainingSummaries = { ...newValue }
      while (Object.keys(remainingSummaries).length > 1) {
        remainingSummaries = chain(remainingSummaries)
          .map((summary, key) => ({ ...summary, key }))
          .orderBy("timestamp", "asc")
          .slice(1)
          .keyBy("key")
          .value()

        if (saveToStorage(remainingSummaries)) {
          newValue = remainingSummaries
          setError(null)
          break
        }
      }
    }

    setSummaries(newValue)
  }, [])

  // Check if the storage is out of sync with the state version
  useEffect(() => {
    function syncLocalstorageAndStateSummaries() {
      const localStorage = getLocalStorageSummaries()
      if (!isEqual(localStorage, summaries)) {
        setSummaries(localStorage)
      }
    }

    window.addEventListener("storage", syncLocalstorageAndStateSummaries)

    return () => {
      window.removeEventListener("storage", syncLocalstorageAndStateSummaries)
    }
  }, [summaries])

  return [summaries, setLocalStorageSummary, error]
}
